/**
 * Hook for prefetching company logos to improve perceived performance
 */
import { useEffect } from 'react';
import { CompanyLogoHelper } from '../utils/CompanyLogoHelper.js';

interface Company {
  companyName?: string;
  id?: number | string;
}

interface UsePrefetchLogosOptions {
  /**
   * Additional companies to prefetch (e.g., from next page of data)
   */
  additionalCompanies?: Company[];
  /**
   * Delay in milliseconds before starting prefetch to prioritize visible content
   */
  delay?: number;
  /**
   * Whether prefetching is enabled
   */
  enabled?: boolean;
  /**
   * Maximum number of logos to prefetch to avoid overwhelming the browser
   */
  maxPrefetch?: number;
}

/**
 * Prefetches company logos in the background to improve user experience
 * @param companies - Array of companies to prefetch logos for
 * @param options - Configuration options
 */
export function usePrefetchLogos(
  companies: Company[],
  options: UsePrefetchLogosOptions = {},
): void {
  const {
    additionalCompanies = [],
    delay = 100,
    enabled = true,
    maxPrefetch = 50, // Increased from 20 for better prefetching
  } = options;

  useEffect(() => {
    if (!enabled || companies.length === 0) {
      return;
    }

    // Delay prefetching to avoid interfering with initial page load
    const timeoutId = setTimeout(() => {
      // Combine current companies with additional companies (e.g., next page)
      const allCompanies = [
        ...companies,
        ...additionalCompanies,
      ];
      const companiesToPrefetch = allCompanies.slice(0, maxPrefetch);

      for (const [
        index,
        company,
      ] of companiesToPrefetch.entries()) {
        // Stagger the prefetch requests to avoid overwhelming the browser
        // Reduce stagger delay for faster prefetching
        const staggerDelay = index * 25;

        setTimeout(() => {
          prefetchCompanyLogo(company.id, company.companyName);
        }, staggerDelay);
      }
    }, delay);

    return (): void => {
      clearTimeout(timeoutId);
    };
  }, [
    companies,
    additionalCompanies,
    maxPrefetch,
    delay,
    enabled,
  ]);
}

/**
 * Prefetches a single company logo by creating hidden Image elements
 * @param companyId - The company ID to prefetch logo for
 * @param companyName - The company name to prefetch logo for
 */
function prefetchCompanyLogo(companyId?: number | string, companyName?: string): void {
  if (!companyName && !companyId) {
    return;
  }

  const fallbackUrls = CompanyLogoHelper.getFallbackUrls(companyId, companyName);

  // Try to prefetch the first URL (most likely to succeed)
  if (fallbackUrls.length > 0) {
    const img = new Image();

    // Set up success handler to try next URL if first one fails
    img.addEventListener('error', (): void => {
      if (fallbackUrls.length > 1) {
        const secondImg = new Image();
        secondImg.src = fallbackUrls[1]; // Try second URL

        // Don't chain further to avoid too many requests
      }
    });

    // Start prefetching
    img.src = fallbackUrls[0];
  }
}

export default usePrefetchLogos;
